import { useCallback, useSyncExternalStore } from "react";

/**
 * Which image's languages are open, per interaction, for this session. Shared by the preview (clicking
 * a quiz tile selects it) and the properties column (the strip). Nothing is selected until someone
 * picks an image, so no language thumbnails load before they are wanted; the pick is remembered when
 * the user comes back to the interaction. Deliberately not in Redux: it is view state, never saved
 * and never undone.
 */
const selected = new Map<string, string>();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const selectLocalizedImage = (interactionId: string, imageId: string) => {
  if (selected.get(interactionId) === imageId) return;
  selected.set(interactionId, imageId);
  listeners.forEach((listener) => listener());
};

/** For tests: forget every pick. */
export const resetLocalizedImageSelection = () => {
  selected.clear();
  listeners.forEach((listener) => listener());
};

export const useLocalizedImageSelection = (interactionId: string | undefined) => {
  const imageId = useSyncExternalStore(subscribe, () => (interactionId ? selected.get(interactionId) : undefined));
  const select = useCallback(
    (id: string) => {
      if (interactionId) selectLocalizedImage(interactionId, id);
    },
    [interactionId]
  );
  return [imageId, select] as const;
};
